import { useRequiredContext } from "@redotech/react-util/context";
import { LoadState, useLoad } from "@redotech/react-util/load";
import { ValidatedConciergeTreatment } from "@redotech/redo-model/ab-tests/concierge";
import { CONCIERGE_TREATMENT_KEY } from "@redotech/redo-model/shopify/cart";
import { CONCIERGE_TREATMENT_ID_QUERY_PARAM } from "@redotech/redo-web/utils/shared-conf";
import { useContext, useEffect } from "react";
import { RedoShopifyRpcClientContext } from "../client/redo-shopify-rpc-client";
import { ExtensionShopifyClientContext } from "../shopify";
import { getConciergeTreatment } from "../utils/treatment-client";

export function useConciergeTreatment({
  currentUrl,
}: {
  currentUrl: URL;
}): LoadState<ValidatedConciergeTreatment | undefined> {
  const extensionShopifyClient = useContext(ExtensionShopifyClientContext);
  const rpcClient = useRequiredContext(RedoShopifyRpcClientContext);

  const conciergeTreatmentLoad = useLoad(async () => {
    const urlParams = new URLSearchParams(currentUrl.search);
    const urlConciergeTreatmentId = urlParams.get(
      CONCIERGE_TREATMENT_ID_QUERY_PARAM,
    );

    const conciergeTreatment = await getConciergeTreatment(rpcClient, {
      desiredTreatmentId: urlConciergeTreatmentId
        ? urlConciergeTreatmentId
        : undefined,
    });

    return conciergeTreatment;
  }, []);

  const cartLoad = useLoad(async () => {
    if (!extensionShopifyClient) {
      return;
    }

    return extensionShopifyClient.cartGet();
  }, [extensionShopifyClient]);

  useEffect(() => {
    const syncConciergeTreatmentCartAttribute = async () => {
      if (!cartLoad.value || !extensionShopifyClient) {
        return;
      }

      const cart = cartLoad.value;
      if (conciergeTreatmentLoad.value) {
        const conciergeTreatmentId = conciergeTreatmentLoad.value.id;
        if (
          !cart.attributes ||
          cart.attributes[CONCIERGE_TREATMENT_KEY] !== conciergeTreatmentId
        ) {
          await extensionShopifyClient.updateCartAttribute({
            attributes: [
              { key: CONCIERGE_TREATMENT_KEY, value: conciergeTreatmentId },
            ],
          });
        }
      }
    };

    void syncConciergeTreatmentCartAttribute();
  }, [conciergeTreatmentLoad.value, cartLoad.value, extensionShopifyClient]);

  return conciergeTreatmentLoad;
}
